import React, { useState } from "react";
import {
  Box,
  DialogContent,
  Typography,
  MenuItem,
  DialogActions,
  Grid,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useQuery, useMutation, useQueryClient } from "react-query";
import CommonDialog from "app/components/common/Dialogs/CommonDialog";
import { LoadingButton } from "@mui/lab";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import {
  CustomSelectBox,
  CustomTextField,
  CustomTextFieldMultiline,
} from "assets/styles/globalStyledComponent";
import { MenuProps } from "app/utils/helper";
import { createTicket } from "app/services/schoolReportingServices/zohoDesk.service";
import { getModuleWithScreenNameList } from "app/services/schoolReportingServices/access.service";
import useFolderWiseFileUploader from "app/hooks/upload/useFolderWiseFileUploader";

const validationSchema = yup.object({
  module: yup.string().required("Module is required"),
  screenName: yup.string().required("Screen Name is required"),
  priority: yup.string().required("Priority is required"),
  subject: yup.string().required("Subject is required"),
  description: yup.string().required("Description is required"),
});

const CreateTicket = ({
  open = false,
  onClose = () => {},
  onTicketCreated = () => {},
}) => {
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const queryClient = useQueryClient();
  const { handleUploadImage } = useFolderWiseFileUploader();

  // Fetch modules and screen names using useQuery
  const {
    data: moduleScreenData,
    isLoading: isModuleLoading,
    error: moduleError,
  } = useQuery({
    queryKey: ["moduleWithScreenNameList"],
    queryFn: async () => {
      const res = await getModuleWithScreenNameList();
      return res;
    },
    // Handles both { moduleList: [...] } and { data: { moduleList: [...] } }
    select: (data) => data?.moduleList || data?.data?.moduleList || [],
    enabled: open, // Fetch only when dialog is open
  });

  // Formik setup
  const {
    values,
    handleChange,
    setValues,
    touched,
    errors,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: {
      module: "",
      screenName: "",
      priority: "",
      subject: "",
      description: "",
      attachments: [],
    },
    validationSchema,
    onSubmit: async (formValues) => {
      createTicketMutation.mutate(formValues);
    },
  });

  // Mutation for creating a ticket
  const createTicketMutation = useMutation({
    mutationFn: async (formValues) => {
      const attachmentUrls = [];
      if (attachmentFiles.length > 0) {
        const uploadPromises = attachmentFiles.map((file) =>
          handleUploadImage(
            false,
            { target: { files: [file] } },
            "withoutSession",
            "support",
            "helpdesk"
          )
        );
        const results = await Promise.all(uploadPromises);
        results.forEach((result) => {
          if (result?.fileURL) {
            attachmentUrls.push(result.fileURL);
          }
        });
        if (attachmentUrls.length !== attachmentFiles.length) {
          throw new Error("Some files failed to upload. Please try again.");
        }
      }

      const ticketData = {
        ...formValues,
        attachments: attachmentUrls,
        departmentId: "201647000000327031", // Example department ID
      };

      return createTicket(ticketData);
    },
    onSuccess: () => {
      toast.success("Ticket created successfully");
      queryClient.invalidateQueries(["tickets"]); // Invalidate ticket list query
      onTicketCreated(); // Call parent refetch only when ticket is successfully created
      handleClose();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create ticket");
    },
  });

  // Handle module change to reset screenName and update available screen names
  const handleModuleChange = (e) => {
    handleChange(e);
    setValues((prev) => ({
      ...prev,
      module: e.target.value,
      screenName: "", // Reset screenName when module changes
    }));
  };

  // Get screen names for the selected module
  const selectedModule = moduleScreenData?.find(
    (item) => item.module === values.module
  );

  const screenNames = selectedModule?.screenNameList || [];

  const handleClose = () => {
    resetForm();
    setAttachmentFiles([]);
    onClose();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setAttachmentFiles((prev) => [...prev, ...files]);
    setValues((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...files.map((file) => file.name)],
    }));
  };

  const removeAttachment = (index) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
    setValues((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  return (
    <CommonDialog
      title="Create Support Ticket"
      open={open}
      onClose={handleClose}
      minWidth="600px"
    >
      <DialogContent>
        <form onSubmit={handleSubmit} id="create-ticket-form">
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={6}>
              <TextFieldLabel title="Module" />
              <CustomSelectBox
                MenuProps={MenuProps}
                fullWidth
                size="small"
                name="module"
                onChange={handleModuleChange}
                value={values.module}
                displayEmpty
                error={touched?.module && Boolean(errors.module)}
                disabled={isModuleLoading || createTicketMutation.isLoading}
              >
                <MenuItem value="" disabled>
                  {isModuleLoading ? "Loading..." : "Select Module"}
                </MenuItem>
                {!isModuleLoading && (moduleScreenData || []).length === 0 && (
                  <MenuItem value="" disabled>
                    No modules found
                  </MenuItem>
                )}
                {(moduleScreenData || []).map((item, index) => (
                  <MenuItem value={item.module} key={index}>
                    {item.module}
                  </MenuItem>
                ))}
              </CustomSelectBox>
              {moduleError && (
                <Typography color="error" variant="caption">
                  Failed to load modules
                </Typography>
              )}
              {touched?.module && errors.module && (
                <Typography color="error" variant="caption">
                  {errors.module}
                </Typography>
              )}
            </Grid>

            <Grid item xs={6}>
              <TextFieldLabel title="Screen Name" />
              <CustomSelectBox
                MenuProps={MenuProps}
                fullWidth
                size="small"
                name="screenName"
                onChange={handleChange}
                value={values.screenName}
                displayEmpty
                error={touched?.screenName && Boolean(errors.screenName)}
                disabled={
                  isModuleLoading ||
                  createTicketMutation.isLoading ||
                  !values.module
                }
              >
                <MenuItem value="" disabled>
                  {values.module ? "Select Screen" : "Select Module First"}
                </MenuItem>
                {screenNames.map((item, index) => (
                  <MenuItem value={item} key={index}>
                    {item}
                  </MenuItem>
                ))}
              </CustomSelectBox>
              {moduleError && (
                <Typography color="error" variant="caption">
                  Failed to load screen names
                </Typography>
              )}
              {touched?.screenName && errors.screenName && (
                <Typography color="error" variant="caption">
                  {errors.screenName}
                </Typography>
              )}
            </Grid>

            <Grid item xs={6}>
              <TextFieldLabel title="Priority" />
              <CustomSelectBox
                MenuProps={MenuProps}
                fullWidth
                size="small"
                name="priority"
                onChange={handleChange}
                value={values.priority}
                displayEmpty
                error={touched?.priority && Boolean(errors.priority)}
                disabled={createTicketMutation.isLoading}
              >
                <MenuItem value="" disabled>
                  Select Priority
                </MenuItem>
                {["High", "Medium", "Low"].map((item, index) => (
                  <MenuItem value={item} key={index}>
                    {item}
                  </MenuItem>
                ))}
              </CustomSelectBox>
              {touched?.priority && errors.priority && (
                <Typography color="error" variant="caption">
                  {errors.priority}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextFieldLabel title="Subject" />
              <CustomTextField
                fullWidth
                size="small"
                placeholder="Enter subject"
                name="subject"
                onChange={handleChange}
                value={values.subject}
                error={touched?.subject && Boolean(errors.subject)}
                helperText={touched?.subject && errors.subject}
                disabled={createTicketMutation.isLoading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextFieldLabel title="Description" />
              <CustomTextFieldMultiline
                fullWidth
                size="small"
                placeholder="Describe the issue"
                name="description"
                onChange={handleChange}
                value={values.description}
                multiline
                minRows={3}
                maxRows={4}
                error={touched?.description && Boolean(errors.description)}
                helperText={touched?.description && errors.description}
                disabled={createTicketMutation.isLoading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextFieldLabel title="Attachments" />
              <Box>
                <input
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="attachment-input"
                  disabled={createTicketMutation.isLoading}
                />
                <label htmlFor="attachment-input">
                  <LoadingButton
                    component="span"
                    variant="outlined"
                    size="small"
                    color="primary"
                    disabled={createTicketMutation.isLoading}
                  >
                    Attach Files
                  </LoadingButton>
                </label>

                {attachmentFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Attached Files:
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {attachmentFiles.map((file, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                            {file.name}
                          </Typography>
                          <LoadingButton
                            size="small"
                            onClick={() => removeAttachment(index)}
                            color="error"
                            disabled={createTicketMutation.isLoading}
                          >
                            Remove
                          </LoadingButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          color="secondary"
          size="large"
          type="submit"
          form="create-ticket-form"
          loading={createTicketMutation.isLoading}
          disabled={createTicketMutation.isLoading || isModuleLoading}
        >
          Create Ticket
        </LoadingButton>
      </DialogActions>
    </CommonDialog>
  );
};

export default CreateTicket;
